import Vue from 'vue';
import * as types from '../mutation-types';
import { prepareVariables, isSceneVar, castInVariable } from '@/utils/variables';
import config from '@/config';
import { loadDataForSnapshot } from '@/utils/loading';
import sendAction from '@/services/icool-backend/api';


const state = {
  vars: {}, // { var id: var´s data }
  groupedVars: {}, // { group.id: vars ids array }
};

const mutations = {
  [types.SET_VARS]: (state, vars) => {
    const result = prepareVariables(vars);
    state.vars = result.vars;
    state.groupedVars = result.groups;
  },
  [types.MARK_VAR_AS_WAITING]: (state, id) => {
    state.vars[id].isWaiting = true;
  },
  [types.MARK_VAR_AS_UPTODATE]: (state, id) => {
    state.vars[id].isWaiting = false;
  },
  [types.SET_VARS_VALUE]: (state, { id, value }) => {
    state.vars[id].value = value;
  },
  [types.SET_RAW_VARS_VALUES]: (state, { varItems }) => {
    varItems.forEach((varItem) => {
      const oldVarItem = state.vars[varItem.id];
      if (oldVarItem) {
        oldVarItem.value = castInVariable(varItem.value, oldVarItem.type);
      }
    });
  },
  [types.SET_VARS_LOGGING_ENABLED]: (state, { varItem, value }) => {
    varItem.logging_enabled = value;
  },
};

const getters = {
  getVars: state => Object.values(state.vars),
  getGroupedVars: state => state.groupedVars,
  getVarById: state => varId => state.vars[varId],
  getControlVarsByStatusVar: (state, getters, rootState, rootGetters) => (statusVar) => {
    const statusActor = rootGetters.getActorById(statusVar.actorId);
    const controlActor = rootGetters.getActorById(statusActor.superiorActorId);
    if (controlActor) {
      return rootGetters.getActorVars(controlActor);
    }
    return null;
  },
  getStatusVarByControlActorId: (state, getters, rootState, rootGetters) => (controlActorId) => {
    const controlActor = rootGetters.getActorById(controlActorId);
    return getters.getStatusVarByControlActor(controlActor);
  },
  getStatusVarByControlActor: (state, getters, rootState, rootGetters) => (controlActor) => {
    const statusActor = rootGetters.getActorById(controlActor.targetActorId);
    if (statusActor) {
      return state.vars[statusActor.singleVarId];
    }
    return null;
  },
  getServiceVars: (state, getters, rootState, rootGetters) => serviceId => (
    getters.getVarsForActors(rootGetters.getActorsByServiceId(serviceId))
  ),
  getServiceSettingVars: (state, getters, rootState, rootGetters) => serviceId => (
    getters.getVarsForActors(rootGetters.getSettingsActors(serviceId))
  ),
  getSettingsVarsGroups: (state, getters) => serviceId => getters.getServiceSettingVars(
    serviceId,
  ).reduce((groups, currentVarItem) => {
    if (currentVarItem.group && groups.indexOf(currentVarItem.group) === -1) {
      groups.push(currentVarItem.group);
    }
    return groups;
  }, []),
  getSettingsVarsForGroup: (state, getters) => (serviceId, group) => getters.getServiceSettingVars(
    serviceId,
  ).filter(varItem => varItem.group === group),
  getVarsForActors: (state, getters, rootState, rootGetters) => actors => actors.reduce(
    (vars, actor) => vars.concat(rootGetters.getActorVars(actor)), [],
  ),
  getWeekSchedulerVars: (state, getters, rootState, rootGetters) => (settingsVar) => {
    const actor = rootGetters.getActorById(settingsVar.actorId);
    return rootGetters.getActorVars(actor).filter(
      varItem => varItem.lft > settingsVar.lft
        && varItem.rght < settingsVar.rght
        && settingsVar.treeId === varItem.treeId,
    );
  },
  getLoggedVars: (state, getters, rootState, rootGetters) => {
    const user = rootState.user.userData;
    let excludeSettingsVars = true;
    if ((user.enable_instaler_level || user.superuser) && user.show_instaler_level) {
      excludeSettingsVars = false;
    }
    return getters.getVars.filter(
      (varItem) => {
        if (varItem.last_log_entry_at) {
          return !(varItem.control_group === 'settings' && excludeSettingsVars);
        }
        return false;
      },
    );
  },
  getLoggingAllowedVars: (state, getters) => serviceId => getters.getVars.filter(
    varItem => varItem.logging_allowed && varItem.serviceId === serviceId,
  ),
  getLoggingEnabledVars: (state, getters) => getters.getVars.filter(
    varItem => varItem.logging_enabled,
  ),
  getWeekSchedulerObject: (state, getters) => (settingsVar) => {
    // structure should be:
    // [
    //   [{ value, time: "T#0s" }, ...],
    //   ...
    // ]
    const plannerVars = getters.getWeekSchedulerVars(settingsVar);
    const days = [];
    plannerVars.filter(varItem => varItem.parentId === settingsVar.id).forEach((dayVar, index) => {
      if (dayVar.name === '0') {
        return;
      }
      const day = {
        varItem: dayVar,
        points: [],
      };
      plannerVars.filter(
        varItem => varItem.parentId === dayVar.id,
      ).sort((a, b) => a.order - b.order).forEach((changePointVar) => {
        const changePointObj = {};
        plannerVars.filter(
          varItem => varItem.parentId === changePointVar.id,
        ).forEach((valVar) => {
          if (valVar.name === 'T_ON') {
            changePointObj.time = valVar;
          }
          if (valVar.name === 'VAL') {
            changePointObj.value = valVar;
          }
          if (valVar.name === 'ENABLE') {
            changePointObj.enable = valVar;
          }
        });
        day.points.push(changePointObj);
      });
      days.push(day);
    });
    return days;
  },
  getOTEPriceChartObject: (state, getters) => (actor) => {
    const vars = getters.getVarsForActors([actor]);

    const values = {};
    const oteData = {};

    vars.filter(varItem => varItem.level === 0).forEach((rootVarItem) => {
      if (rootVarItem.name === 'EUR') {
        oteData.eur = rootVarItem.value;
      } else if (rootVarItem.name === 'NUM_OF_VAL') {
        oteData.length = rootVarItem.value;
      } else if (rootVarItem.name === 'FE_ARRAY') {
        vars.filter(varItem => varItem.parentId === rootVarItem.id).forEach((arrayVarItem) => {
          values[arrayVarItem.id] = {};
        });
        vars.forEach((varItem) => {
          if (varItem.name === 'PRICE') {
            values[varItem.parentId].price = varItem;
          } else if (varItem.name === 'HOUR_ON') {
            values[varItem.parentId].hourOn = varItem;
          } else if (varItem.name === 'HOUR_ACT') {
            values[varItem.parentId].hourAct = varItem;
          }
        });
      }
    });

    oteData.values = Object.values(values).filter(value => value.hourAct.value !== 0).sort((a, b) => a.hourAct - b.hourAct);

    return oteData;
  },
  getEnergoWidgetVars: (state, getters, rootState, rootGetters) => {
    const service = _.last(rootGetters.getServices.filter(item => item.icon === 'Energy'));
    if (service) {
      const actor = rootGetters.getActorByArgumentName(service.id, 'ENERGO_WIDGET');
      return getters.getVarsForActors([actor]);
    }
    return null;
  },
  getEnergoWidgetObject: (state, getters) => {
    const vars = getters.getEnergoWidgetVars;
    if (vars) {
      const energoData = {};

      const funcToPropMap = {
        CURRENT_GRID: (varItem) => { energoData[varItem.parentId].currentGrid = varItem.value; },
        CURRENT_PVE: (varItem) => { energoData[varItem.parentId].currentPVE = varItem.value; },
        CURRENT_STORAGE: (varItem) => { energoData[varItem.parentId].currentStorage = varItem.value; },
        DEVICE_TYPE: (varItem) => { energoData[varItem.parentId].deviceType = varItem; },
        TOTAL_GRID: (varItem) => { energoData[varItem.parentId].totalGrid = varItem.value; },
        TOTAL_PVE: (varItem) => { energoData[varItem.parentId].totalPVE = varItem.value; },
        TOTAL_STORAGE: (varItem) => { energoData[varItem.parentId].totalStorage = varItem.value; },
      };

      vars.filter(varItem => varItem.level === 0).forEach((rootVarItem) => {
        energoData[rootVarItem.id] = { varItem: rootVarItem };
      });
      vars.forEach((varItem) => {
        if (varItem.level === 1) {
          funcToPropMap[varItem.name](varItem);
        }
      });
      return Object.values(energoData);
    }
    return null;
  },
};

const actions = {
  async loadVars({ commit, rootState }) {
    return loadDataForSnapshot(commit, rootState, types.SET_VARS, config.ICOOL.API.VARS_PATH);
  },
  setVarsLoggingEnabled({ commit }, payload) {
    commit(types.SET_VARS_LOGGING_ENABLED, payload);
  },
  updateVarsValues({ commit, dispatch }, { varItems }) {
    commit(types.SET_RAW_VARS_VALUES, { varItems });
  },
  setVarsValue({ commit, dispatch }, { varItem, value }) {
    Vue.$log.info('setting var value', { varItem, value });
    if (isSceneVar(varItem)) {
      dispatch('updateSceneVarValue', { varItem, value });
    } else {
      commit(types.SET_VARS_VALUE, { id: varItem.id, value });
      dispatch('processAction', varItem);
    }
  },
  processAction({ commit, dispatch }, varItem) {
    commit(types.MARK_VAR_AS_WAITING, varItem.id);
    sendAction(varItem).then(() => {});
  },
  SOCKET_DATA_UPDATED({ commit, getters }, msg) {
    const data = JSON.parse(msg);
    const varItem = getters.getVarById(data.id);
    const controlVars = getters.getControlVarsByStatusVar(varItem);
    commit(types.SET_VARS_VALUE, { id: varItem.id, value: castInVariable(data.value, varItem.type) });
    commit(types.MARK_VAR_AS_UPTODATE, varItem.id);
    if (controlVars) {
      controlVars.forEach((controlVar) => {
        commit(types.MARK_VAR_AS_UPTODATE, controlVar.id);
      });
    }
    Vue.$log.debug(data);
  },
};

export default {
  state,
  mutations,
  getters,
  actions,
};
